import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { FileAttachmentThemeType, FileDTO } from "../../types";
import { convertToFilesToFileDTOs } from "../../utils";
import BrowseFiles from "./BrowseFiles";
import FileTable from "./FileTable";

export type ColumnType = "fileName" | "lastModified" | "size" | "fileType";

type FileAttachmentProps = {
  /** List of files currently selected */
  files: File[];
  /** Function for change of selected files */
  onChange: (value: File[]) => void;
  /** accepted file types: comma seperated string or array of strings */
  fileTypes?: string | string[];
  /** if the sub table should be shown */
  showTable?: boolean;
  /** if the input is loading */
  loading?: boolean;
  /** if the input is disabled */
  disabled?: boolean;
  /** max number of files allowed */
  maxFiles?: number;
  /** max number of bytes allowed for a file */
  maxFileSize?: number;
  /** placeholder for the input */
  placeholder?: string;
  /** Columns to display in table */
  displayColumns?: ColumnType[];
  /** className to pass to the component */
  className?: string;
  /** if the component is read only */
  readOnly?: boolean;
  /** overrideable styles */
  styles?: FileAttachmentThemeType;
  /** Text to override browse button */
  browseButtonText?: React.ReactNode;
};

/**
 * File Attachment component allowing for single file upload or multiple files
 */
const FileAttachment: React.FC<React.PropsWithChildren<FileAttachmentProps>> = ({
  files = [],
  onChange,
  fileTypes = [],
  disabled = false,
  showTable = true,
  loading = false,
  maxFiles = 1,
  maxFileSize,
  placeholder,
  displayColumns = ["fileName", "lastModified", "size", "fileType"],
  className = "",
  styles,
  browseButtonText = "Browse",
  readOnly = false,
}) => {
  const [fileDTOs, setFileDTOs] = useState<FileDTO[]>([]);

  const onFileAdd = (newFiles: File[]) => {
    onChange(files.concat(newFiles));
  };

  const onFileDelete = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    onChange(newFiles);
  };

  useEffect(() => {
    setFileDTOs(convertToFilesToFileDTOs(files));
  }, [files]);

  return (
    <div className={clsx(className)}>
      {!disabled && !readOnly && (
        <BrowseFiles
          files={files}
          fileTypes={fileTypes}
          onFileAdd={onFileAdd}
          onFileDelete={onFileDelete}
          maxFiles={maxFiles}
          maxFileSize={maxFileSize}
          placeholder={placeholder}
          styles={styles}
          browseButtonText={browseButtonText}
          disabled={loading}
        />
      )}
      {(maxFiles != 1 || readOnly) && showTable && (
        <FileTable
          files={fileDTOs}
          deleteFile={onFileDelete}
          displayColumns={displayColumns}
          loading={loading}
          readOnly={readOnly}
        />
      )}
    </div>
  );
};

export default FileAttachment;
